/* eslint-disable react/no-unescaped-entities */
import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import DahliasShell from "../components/dahlias/DahliasShell";

/**
 * Heritage Dahlias, Policies & Legal pages.
 *
 * All 9 documents render from a single component to keep things tight.
 * Slugged under /legal/:slug. First-draft NZ-friendly copy, edit freely
 * in this file before publishing.
 */
const TODAY = "February 2026";

const POLICIES = {
  privacy: {
    title: "Privacy Policy",
    kicker: "How we handle your information",
    body: `Heritage Dahlias ("we", "us", "Heritage") is a small family flower farm in Aotearoa. We take privacy seriously and only collect what we need to fulfil your orders and stay in touch.

What we collect
We collect the information you give us when you place an order, register for a Heritage HQ account, join the Bloom Club waitlist, or contact us. Typically your name, email, delivery address, phone, order history, and any messages you send us. Payment card details are processed by Stripe and never touch our servers.

How we use it
We use your information to fulfil and ship your orders, send order updates, manage your Heritage HQ account, respond to your messages, and (with your consent) send occasional newsletters about new varieties or events. We do not sell your information to anyone.

Sharing
We share information only with the third parties that help us run the farm. Our payment processor (Stripe), our email delivery service (Resend), and our shipping carriers, and only what is strictly needed to deliver the service to you.

Your rights
You can ask us to access, correct, or delete the personal information we hold about you at any time by emailing hello@heritagedahlias.nz. We comply with the Aotearoa Privacy Act 2020.

Cookies
The Heritage Dahlias website uses cookies (and similar local storage) to keep you signed into Heritage HQ, remember your cart, and measure basic site traffic. You can clear or block cookies in your browser. The site will still work, but signed-in features will require you to sign in again.

Children
Heritage Dahlias is not directed at children under 16. We do not knowingly collect personal information from children.

Changes to this policy
We may update this policy from time to time. The "Last updated" date below shows when the current version was published.`,
  },
  terms: {
    title: "Terms & Conditions",
    kicker: "The agreement for using our website and store",
    body: `By using the Heritage Dahlias website (heritagedahlias.nz), Heritage HQ portal, or by placing an order with us, you agree to these terms.

Orders
All orders are subject to availability. We reserve the right to refuse or cancel any order. For example if a variety has unexpectedly run out, if there is a pricing error, or if we suspect the order is not genuine. If we cancel a paid order we will refund you in full.

Pricing
All prices are listed in NZD and include GST where applicable. Shipping is charged at the rates published on our Shipping Policy page or quoted at checkout. We may change our prices at any time but never after you have placed an order.

Payment
Payment is made by credit or debit card via Stripe at the time of order, unless other arrangements have been made in writing. Tubers are held against your order from the date of payment.

Cancellations
You can cancel a non-dispatched order by emailing hello@heritagedahlias.nz. Once tubers have been shipped, the Returns Policy applies.

Account responsibilities
If you have a Heritage HQ account you are responsible for keeping your password secure and for everything that happens under your account. Tell us straight away if you think your account has been used without permission.

Liability
To the extent permitted by law, Heritage Dahlias total liability for any one order is limited to the amount you paid for that order. We do not exclude any rights you have under the Consumer Guarantees Act 1993.

Governing law
These terms are governed by the laws of Aotearoa.`,
  },
  shipping: {
    title: "Shipping Policy",
    kicker: "How and when your tubers arrive",
    body: `Heritage Dahlias ships tubers nationwide within Aotearoa. International shipping is not currently available.

Shipping window
Tubers are shipped during the dormant season, typically from 1 September each year, once they have cured and are ready to travel. This window protects the tubers from cold and ensures they arrive in the best possible condition.

Rates
Standard rural and urban shipping is calculated at checkout. VIP Build-A-Box rates are flat. $15 for the 6 pack and $20 for the 12 pack.

Tracking
Once your order ships you will receive an email with the courier tracking number. You can also see the status of every order in your Heritage HQ portal.

Damaged or missing parcels
If your parcel arrives damaged, or hasn't arrived within 10 working days of shipping, email hello@heritagedahlias.nz with your order number. We will work with the courier to find it or send a replacement where possible.

Delivery address
Please make sure your delivery address is correct at checkout. Re shipping costs for incorrect addresses are passed on to the customer.`,
  },
  returns: {
    title: "Returns Policy",
    kicker: "Refunds, replacements & live-tuber guarantee",
    body: `We grow every tuber by hand on our Aotearoa farm and want you to be delighted with what arrives. If anything is wrong, please get in touch.

Live tuber guarantee
We guarantee that every tuber we ship is alive and viable at the time of dispatch. If a tuber arrives soft, mouldy, or visibly damaged, please email hello@heritagedahlias.nz within 7 days of delivery. Include a photo and your order number, and we will arrange a replacement or refund.

Change of mind
Because tubers are a perishable, seasonal product, we cannot accept returns for change of mind once the order has been shipped. Please confirm your varieties carefully before submitting your order or VIP box.

Growing outcomes
We cannot guarantee how a tuber will perform in your garden. Soil, weather, watering, and pests all play a part. The live tuber guarantee covers the condition on arrival only.

Refund timing
Approved refunds are processed back to the original payment method within 5 working days.`,
  },
  ip: {
    title: "Intellectual Property Policy",
    kicker: "Protecting heritage, varieties, photography & data",
    body: `Heritage Dahlias is the result of generations of work, photography, and breeding. All intellectual property generated by Heritage is protected.

Brand & content
The Heritage Dahlias name, seal, logos, illustrations (including the stained glass artwork rendered from our February 2026 summer flat lay), copy, photography, and the structure and design of this website are owned by Heritage Dahlias. You may not copy, redistribute, or republish them without our written permission.

Variety information
The names, descriptions, growing notes, and bloom photographs for each variety in our catalogue are owned by Heritage Dahlias. You may print or save them for personal reference. You may not republish them on commercial nursery websites, retail listings, or competing catalogues.

Bred varieties
Where Heritage Dahlias has bred a unique variety, that name and lineage is ours. Tubers we sell are for personal cultivation. Commercial propagation or sale of Heritage bred varieties under any name requires a written grower agreement (see the Grower Agreement page).

Reporting infringement
If you believe content on this site infringes your rights, email hello@heritagedahlias.nz with details and we will respond promptly.`,
  },
  copyright: {
    title: "Copyright Policy",
    kicker: "Photography, text & illustrated artwork",
    body: `© Heritage Dahlias 1932 to ${new Date().getFullYear()}. All rights reserved.

Heritage photographs, including the original Nana Jean photographs, the bloom and flat lay photography used across the site, and the stained glass illustrations rendered from our February 2026 summer flat lay, are the copyrighted work of Heritage Dahlias and the individual photographers and illustrators we have commissioned.

Permitted use
You may share links to Heritage Dahlias pages on social media and personal blogs, and may quote short excerpts of our text with attribution. You may save bloom photographs for personal garden planning.

Prohibited use
You may not reproduce Heritage photographs, illustrations, or written content on commercial websites, in printed catalogues, in advertising, or in AI training datasets without our written permission.

Permission requests
Email hello@heritagedahlias.nz with the specific work you wish to use, the intended audience, and the proposed credit line.`,
  },
  "grower-agreement": {
    title: "Grower Agreement",
    kicker: "For Heritage Bloom Club growers",
    body: `This agreement applies to anyone accepted into the Heritage Bloom Club grower network. By signing your application and accepting tubers from Heritage Dahlias, you agree to the following.

Scope
Heritage will supply you with a set of named tubers each season. You will grow them on your own land using best practice horticulture and the grower's guide we publish in your portal.

Variety identity
You will keep accurate records of which variety is planted in which row. You will not sell, gift, or distribute Heritage bred varieties to third parties without Heritage's written permission. Tubers may be propagated only for your own next season use unless a separate written arrangement is in place.

Reporting
You agree to log harvest counts, bloom photos, and any anomalies in your grower portal each season so Heritage can keep accurate provenance for every variety.

Payment & supplies
Where the Bloom Club has agreed pricing for blooms or tubers you grow on Heritage's behalf, that pricing is set out in your grower portal. Supplies (labels, ties, etc.) ordered through the portal are billed monthly.

Termination
Either party can end the agreement on 30 days written notice. On termination, any Heritage bred variety tubers remaining in your possession must be returned to Heritage or destroyed.

Confidentiality
You agree to keep Heritage's grower's guide, propagation notes, and pricing confidential.`,
  },
  "customer-agreement": {
    title: "Customer Agreement",
    kicker: "Buying from Heritage Dahlias",
    body: `This agreement applies when you buy tubers, boxes, or other products from Heritage Dahlias.

Order acceptance
Your order is accepted when we send the confirmation email. Until then, we may decline or amend the order. For example if a variety has run out.

Stock holding
Once paid, your tubers are held under your name and shipped during the spring dispatch window (typically from 1 September). They are not available for in-person collection.

VIP Build A Box
Boxes are subject to availability. We reserve the right to substitute a variety of equal or greater value if your chosen variety becomes unavailable before dispatch. We will email you first to confirm any substitution.

Tubers for personal use
Tubers are sold for your own garden. You may grow, divide, and replant them for personal use. You may not commercially propagate Heritage bred varieties without our written permission (see the IP Policy).

Communication
By placing an order you agree that we may contact you about that order. You can opt out of marketing emails at any time from your Heritage HQ profile.

Disputes
Most things can be sorted with a quick email to hello@heritagedahlias.nz. If a dispute can't be resolved that way, it will be determined under the laws of Aotearoa.`,
  },
  "website-usage": {
    title: "Website Usage Terms",
    kicker: "Acceptable use of heritagedahlias.nz",
    body: `By using the Heritage Dahlias website you agree to use it lawfully and in good faith.

You will not
• Scrape, crawl, or republish the catalogue, photography, or illustrated artwork
• Attempt to access another user's Heritage HQ account, or the Admin / Operations / Grower portals
• Disrupt, overload, or attempt to bypass the security of the site
• Use the site to send spam, phishing, or other malicious content
• Submit false orders, false reservation requests, or fraudulent grower applications
• Use Heritage content to train any commercial AI model

You may
• Browse the catalogue freely
• Save bloom photographs for personal garden planning
• Share links to Heritage pages on social media
• Quote short excerpts of our text with attribution

Account security
Keep your password secret. We are not responsible for losses caused by you sharing your login.

Site changes
Heritage may change, suspend, or shut down any part of the site at any time. We aim to keep your data accessible from your Heritage HQ portal whenever possible.`,
  },
};

export default function LegalPage() {
  const { slug } = useParams();
  const policy = POLICIES[slug];

  if (!policy) return <Navigate to="/legal/privacy" replace />;

  return (
    <DahliasShell>
      <article data-testid={`legal-${slug}`} className="bg-[var(--dahlias-cream)] text-[var(--dahlias-ink)]">
        <div className="max-w-3xl mx-auto px-5 md:px-8 py-16 md:py-24">
          <Link to="/" data-testid="legal-back" className="inline-flex items-center gap-2 text-sm text-[#6E1E3A] hover:underline">
            <ArrowLeft className="w-4 h-4" /> Heritage Dahlias
          </Link>
          <header className="mt-8">
            <span className="font-label tracking-[0.4em] text-[11px] uppercase text-[#6E1E3A]">✦ {policy.kicker} ✦</span>
            <h1 className="mt-3 font-display text-5xl md:text-6xl text-[var(--dahlias-forest)] leading-[1.05]">
              {policy.title.split(" ").slice(0, -1).join(" ")}{" "}
              <span className="font-display-italic text-[#6E1E3A]">{policy.title.split(" ").slice(-1)}.</span>
            </h1>
          </header>

          <div className="mt-10 prose prose-lg max-w-none text-[var(--dahlias-ink)]/85 leading-relaxed whitespace-pre-line font-serif">
            {policy.body}
          </div>

          <footer className="mt-14 pt-6 border-t border-[#6E1E3A]/20 text-xs text-[var(--dahlias-ink)]/65 flex flex-wrap items-center justify-between gap-3">
            <span>Last updated: {TODAY}</span>
            <span>Heritage Dahlias · Aotearoa · hello@heritagedahlias.nz</span>
          </footer>

          <nav className="mt-10 grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
            {Object.entries(POLICIES).map(([s, p]) => (
              <Link
                key={s}
                to={`/legal/${s}`}
                data-testid={`legal-nav-${s}`}
                className={`px-3 py-2 wobble-pill border text-center transition ${
                  s === slug
                    ? "bg-[#6E1E3A] text-[#FBE6D8] border-[#6E1E3A]"
                    : "bg-white border-[#6E1E3A]/20 text-[var(--dahlias-forest)] hover:border-[#6E1E3A]/50"
                }`}
              >
                {p.title}
              </Link>
            ))}
          </nav>
        </div>
      </article>
    </DahliasShell>
  );
}

export const LEGAL_SLUGS = Object.keys(POLICIES);
